import { useState } from 'react';
import { Alert, Button, Card, List, Modal, Select, Space, Spin, Switch, Typography } from 'antd';
import { DeleteOutlined } from '@ant-design/icons';
import type { ReactNode } from 'react';
import type { UseQueryResult } from '@tanstack/react-query';
import type { AppFeedbackReviewCandidateDto, AppFeedbackReviewSettingsDto } from 'breuer-supercharged-sdk';
import { describeApiError } from '@/utils/api_error_message';
import {
    autoReleaseConfirmText,
    removesCurrentUserAsReviewer,
    reviewSettingsDirty,
    settingsSavedMessage,
    validateReviewSettings,
    type ReviewSettingsDraft,
} from './feedback_review_utils';

const { Text, Paragraph } = Typography;

type QueryState<T> = Pick<UseQueryResult<T, unknown>, 'data' | 'isLoading' | 'isError' | 'isSuccess' | 'isFetching' | 'refetch'>;

interface FeedbackReviewSettingsFormProps {
    settingsQuery: QueryState<AppFeedbackReviewSettingsDto>;
    candidatesQuery: QueryState<AppFeedbackReviewCandidateDto[]>;
    /** Speichert den Entwurf; wirft bei Fehler (die Meldung zeigt das Formular). */
    onSave: (draft: ReviewSettingsDraft, removesCurrentUser: boolean) => Promise<{ autoReleasedCount: number }>;
    isSaving: boolean;
    /** Eigene Nutzer-ID für die Selbstentfernungs-Warnung; im System-Admin-Kontext leer. */
    currentUserId?: string;
    footerNote?: ReactNode;
}

/** Formular für Vorprüfung + Prüferliste; Datenquelle und Speichern kommen von außen (Verwaltung bzw. System-Admin). */
export default function FeedbackReviewSettingsForm({
    settingsQuery,
    candidatesQuery,
    onSave,
    isSaving,
    currentUserId,
    footerNote,
}: FeedbackReviewSettingsFormProps) {
    const { data: settings, isLoading, isError, refetch: refetchSettings } = settingsQuery;
    const candidates = candidatesQuery.data ?? [];
    const candidatesReady = candidatesQuery.isSuccess;
    const [saveError, setSaveError] = useState<string | null>(null);
    const [savedText, setSavedText] = useState<string | null>(null);
    const [confirmText, setConfirmText] = useState<string | null>(null);

    // Arbeitskopie, übernommen erst bei „Speichern“; seed per Render-Anpassung wie in av_forward_config.tsx.
    const [draft, setDraft] = useState<ReviewSettingsDraft>({ reviewEnabled: false, reviewerUserIds: [] });
    const [seedSettings, setSeedSettings] = useState(settings);
    if (seedSettings !== settings) {
        setSeedSettings(settings);
        if (settings) {
            setDraft({ reviewEnabled: settings.reviewEnabled, reviewerUserIds: settings.reviewerUserIds });
            setSaveError(null);
        }
    }
    const [addPick, setAddPick] = useState<string | undefined>(undefined);

    if (isLoading) return <Spin />;
    if (isError || !settings) return (
            <Alert
                type="error"
                showIcon
                title="Einstellungen konnten nicht geladen werden."
                action={<Button size="small" onClick={() => void refetchSettings()}>Erneut versuchen</Button>}
            />
        );

    const ctx = { currentUserId, savedReviewerUserIds: settings.reviewerUserIds };
    const dirty = reviewSettingsDirty(draft, settings);
    const validationError = validateReviewSettings(draft, ctx);
    const selfRemoval = removesCurrentUserAsReviewer(draft, ctx);

    const nameFor = (userId: string) => {
        const c = candidates.find((x) => x.userId === userId);
        if (c) return { name: c.displayName, email: c.email };
        const r = settings.reviewers.find((x) => x.userId === userId);
        return { name: r?.displayName ?? userId, email: '' };
    };
    const addable = candidates
        .filter((c) => !draft.reviewerUserIds.includes(c.userId))
        .map((c) => ({ value: c.userId, label: `${c.displayName} (${c.email})` }));

    const save = async () => {
        setConfirmText(null);
        setSaveError(null);
        setSavedText(null);
        try {
            const result = await onSave(draft, selfRemoval);
            setSavedText(settingsSavedMessage(result.autoReleasedCount));
        } catch (error) {
            setSaveError(describeApiError('Speichern fehlgeschlagen', error));
        }
    };

    // Rückfrage nur, wenn Speichern offene Meldungen automatisch an die Entwickler weitergibt.
    const handleSave = () => {
        const text = autoReleaseConfirmText(draft, settings.pendingCount);
        if (text) setConfirmText(text);
        else void save();
    };

    return (
        <Space orientation="vertical" size="large" style={{ width: '100%', maxWidth: 720 }}>
            <Card title="Vorprüfung">
                <Space align="start" size="middle">
                    <Switch
                        checked={draft.reviewEnabled}
                        onChange={(reviewEnabled) => setDraft({ ...draft, reviewEnabled })}
                        aria-label="Vorprüfung aktiv"
                    />
                    <div>
                        <Text strong>Vorprüfung aktiv</Text>
                        <Paragraph type="secondary" style={{ margin: 0 }}>
                            Ist sie an, geht neues Feedback zuerst an die Prüfer. Ist sie aus, geht alles wie bisher direkt an die
                            Entwickler.
                        </Paragraph>
                    </div>
                </Space>
            </Card>

            <Card title="Prüfer">
                <Paragraph type="secondary">
                    Alle Prüfer sehen dieselbe Liste, wer zuerst entscheidet, gilt. Für eine Vertretung einfach eine zweite
                    Person eintragen.
                </Paragraph>
                <List
                    bordered
                    locale={{ emptyText: 'Noch kein Prüfer eingetragen' }}
                    dataSource={draft.reviewerUserIds}
                    renderItem={(userId) => {
                        const { name, email } = nameFor(userId);
                        return (
                            <List.Item
                                actions={[
                                    <Button
                                        key="remove"
                                        type="text"
                                        danger
                                        icon={<DeleteOutlined />}
                                        aria-label={`${name} entfernen`}
                                        onClick={() =>
                                            setDraft({ ...draft, reviewerUserIds: draft.reviewerUserIds.filter((id) => id !== userId) })
                                        }
                                    />,
                                ]}
                            >
                                <List.Item.Meta
                                    title={
                                        <>
                                            {name}
                                            {userId === currentUserId && <Text type="secondary"> (Sie)</Text>}
                                        </>
                                    }
                                    description={email || undefined}
                                />
                            </List.Item>
                        );
                    }}
                />
                {candidatesQuery.isError && (
                    <Alert
                        type="error"
                        showIcon
                        style={{ marginTop: 12 }}
                        title="Die Benutzerliste konnte nicht geladen werden."
                        action={
                            <Button size="small" onClick={() => void candidatesQuery.refetch()} loading={candidatesQuery.isFetching}>
                                Erneut versuchen
                            </Button>
                        }
                    />
                )}
                <Select
                    aria-label="Prüfer hinzufügen"
                    loading={candidatesQuery.isLoading}
                    disabled={!candidatesReady}
                    showSearch
                    allowClear
                    value={addPick}
                    placeholder={candidatesQuery.isLoading ? 'Benutzer werden geladen …' : 'Prüfer hinzufügen …'}
                    optionFilterProp="label"
                    options={addable}
                    notFoundContent="Keine weiteren Benutzer verfügbar"
                    style={{ width: '100%', marginTop: 12 }}
                    onChange={(userId?: string) => {
                        if (!userId) return setAddPick(undefined);
                        setDraft({ ...draft, reviewerUserIds: [...draft.reviewerUserIds, userId] });
                        setAddPick(undefined);
                    }}
                />
            </Card>

            {validationError && <Alert type="error" showIcon title={validationError} />}
            {saveError && <Alert type="error" showIcon title={saveError} role="alert" />}
            {savedText && !dirty && (
                <Alert type="success" showIcon title={savedText} role="status" data-testid="feedback-settings-saved" />
            )}
            {!validationError && selfRemoval && (
                <Alert type="warning" showIcon title="Sie verlieren danach den Zugriff auf diese Seite." />
            )}

            {footerNote}

            <Space>
                <Button type="primary" onClick={handleSave} loading={isSaving} disabled={!dirty || !!validationError || !candidatesReady}>
                    Speichern
                </Button>
                {dirty && (
                    <Button
                        onClick={() => setDraft({ reviewEnabled: settings.reviewEnabled, reviewerUserIds: settings.reviewerUserIds })}
                        disabled={isSaving}
                    >
                        Zurücksetzen
                    </Button>
                )}
            </Space>

            <Modal
                open={confirmText !== null}
                title="Vorprüfung abschalten?"
                okText="Speichern und weitergeben"
                cancelText="Abbrechen"
                onOk={() => void save()}
                onCancel={() => setConfirmText(null)}
                confirmLoading={isSaving}
            >
                {confirmText}
            </Modal>
        </Space>
    );
}
